import React, {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';
import { Swiper, SwiperSlide } from 'swiper/react';
import { Swiper as SwiperClass } from 'swiper';
import { Keyboard, Navigation, Pagination } from 'swiper/modules';
import { SlideItem } from '@/types/common';

interface ObjectSliderModalProps {
  active?: number | null;
  slides?: SlideItem[];
}

const modalClassName = 'object-modal-slider';

const ObjectSliderModal = ({
  active = null,
  slides = []
}: ObjectSliderModalProps) => {
  const swiperRef = useRef<any>(null);

  const [index, setIndex] = useState<number | null>(null);
  const realIndexRef = useRef<number | null>(null);

  const onInit = useCallback((swiper: SwiperClass) => {
    swiperRef.current = swiper;
  }, []);

  const onSlideChange = useCallback((swiper: SwiperClass) => {
    realIndexRef.current = swiper.realIndex;
  }, []);

  useEffect(() => {
    if (active != null) {
      swiperRef.current?.slideToLoop(active, 0);
    }
  }, [active]);

  useEffect(() => {
    type SwiperElement = (Element & { swiper: SwiperClass });
    const swiperEl = document.querySelector('.swiper') as SwiperElement | null;

    if (!swiperEl) {
      return;
    }

    const { swiper } = swiperEl;
    // `active`, when given, is the slide we were explicitly asked to open on (e.g. the
    // Plan button) — prefer it over the main slider's current position, which is only a
    // sensible default when no specific slide was requested.
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setIndex(active ?? swiper.realIndex);

    swiper.keyboard.disable();

    return () => {
      swiper.keyboard.enable();
      swiper.slideToLoop(realIndexRef.current ?? 0, 0);
    };
  }, []);

  if (index == null) {
    return null;
  }

  return (
    <div className={modalClassName}>
      <Swiper
        loop
        navigation={{
          enabled: true,
          nextEl: `.${modalClassName} .swiper-button-next`,
          prevEl: `.${modalClassName} .swiper-button-prev`
        }}
        initialSlide={index}
        keyboard
        pagination={{
          enabled: true,
          el: `.${modalClassName} .swiper-pagination`,
          type: 'fraction'
        }}
        slidesPerView={1}
        modules={[
          Keyboard,
          Navigation,
          Pagination
        ]}
        onInit={onInit}
        onSlideChange={onSlideChange}
      >
        {slides.map((slide) => (
          <SwiperSlide key={slide.src}>
            <img
              alt={slide.alt}
              className="swiper-slide-image"
              draggable={false}
              sizes="100vw"
              src={slide.src}
              srcSet={slide.srcSet}
            />
          </SwiperSlide>
        ))}
      </Swiper>

      <div className="swiper-button-prev" />
      <div className="swiper-button-next" />
      <div className="swiper-pagination" />
    </div>
  );
};

export default ObjectSliderModal;
